import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BACKEND_URL } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Send, ImagePlus, X } from "lucide-react";

const TYPES = ["image/png", "image/jpeg", "image/webp"];

const Bubble = ({ m, mine }) => (
  <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-amber-500/15 border border-amber-500/30" : "bg-muted"}`}>
      <div className="text-[10px] text-muted-foreground mb-0.5">
        {m.from === "admin" ? "Conecta Mais" : m.name} · {new Date(m.created_at).toLocaleString("pt-BR")}
      </div>
      {m.image_url && (
        <a href={`${BACKEND_URL}${m.image_url}`} target="_blank" rel="noreferrer" data-testid="message-image-link">
          <img src={`${BACKEND_URL}${m.image_url}`} alt="Foto enviada" loading="lazy"
               className="rounded-lg max-h-56 w-auto object-cover mb-1" data-testid="message-image" />
        </a>
      )}
      {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
    </div>
  </div>
);

export const MessageThread = ({ messages, viewer, onSend, testid }) => {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!file) { setPreview(null); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => { URL.revokeObjectURL(url); };
  }, [file]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!TYPES.includes(f.type)) { toast.error("Envie uma imagem PNG, JPG ou WEBP"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("A imagem deve ter no máximo 5 MB"); return; }
    setFile(f);
  };

  const send = async () => {
    if (!text.trim() && !file) return;
    const fd = new FormData();
    fd.append("text", text.trim());
    if (file) fd.append("file", file);
    setBusy(true);
    try { if (await onSend(fd)) { setText(""); setFile(null); } }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={testid}>
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid={`${testid}-messages`}>
        {messages.length === 0 && <p className="text-xs text-muted-foreground">Nenhuma mensagem ainda.</p>}
        {messages.map((m, i) => <Bubble key={`${m.created_at}-${i}`} m={m} mine={m.from === viewer} />)}
      </div>
      {preview && (
        <div className="relative inline-block mt-3" data-testid={`${testid}-preview`}>
          <img src={preview} alt="Pré-visualização" className="h-20 rounded-lg border border-border object-cover" />
          <button type="button" onClick={() => setFile(null)} className="absolute -top-2 -right-2 rounded-full bg-rose-500 text-white p-0.5"
                  aria-label="Remover foto" data-testid={`${testid}-remove-image`}>
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
      <div className="flex gap-2 mt-3">
        <input ref={inputRef} type="file" accept={TYPES.join(",")} className="hidden" onChange={pick} data-testid={`${testid}-file-input`} />
        <Button type="button" variant="outline" className="self-end" onClick={() => inputRef.current?.click()}
                title="Anexar foto" data-testid={`${testid}-attach`}>
          <ImagePlus className="h-4 w-4" />
        </Button>
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)}
                  placeholder={viewer === "admin" ? "Ex.: Por favor, informe seu endereço completo e tamanho." : "Escreva sua resposta..."}
                  data-testid={`${testid}-input`} />
        <Button className="gold-btn self-end" disabled={busy || (!text.trim() && !file)} onClick={send} data-testid={`${testid}-send`}>
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
